import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "head-wrapper" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "diamond-head-background" }, null, -1)
const _hoisted_3 = { class: "diamond areaDiamond" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "diamondCrosshairHorizontal" }, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "diamondCrosshairVertical" }, null, -1)
const _hoisted_6 = { key: 0 }
const _hoisted_7 = ["onClick", "href", "aria-label"]
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "diamond-button-hit-target" }, null, -1)
const _hoisted_9 = [
  _hoisted_8
]

import { ref, computed, watch } from "vue";
import { TimelineMax, Back } from "gsap";
import { Tooltip } from "uiv";
import useDiamondHeadModel from "../composables/useDiamondHeadModel";
import {
  HeadApiModel,
  TeamEffectivenessRatingsApiModel,
  TeamEffectivenessReviewRatingsApiModel,
} from "../api-models";
import cms from "../services/cms/cms";
import cmsContentMap from "../services/cms/cms-content-map";
import { CmsTeamEffectivenessReview } from "../services/cms/cms-type-definitions";
import HeadFromModel from "./head/head-from-model.vue";
import { getTeamEffectivenessReport } from "./team-effectiveness/get-team-effectiveness-report-team-id";

type Ratings = TeamEffectivenessReviewRatingsApiModel | TeamEffectivenessRatingsApiModel | null;

const html = "";

export default /*#__PURE__*/_defineComponent({
  __name: 'diamond-head',
  props: {
    headId: { default: null },
    kind: { default: "TeamEffectivenessAggregateDiamond" },
    ratings: { default: null },
    clickable: { type: Boolean, default: true },
    tooltips: { type: Boolean, default: true },
    animateEntrance: { type: Boolean, default: false }
  },
  emits: ["leader-type-selection", "onDataLoaded"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const diamondButtonRef = ref<HTMLElement | null>(null);

const {
  getSoloHeadModel,
  getViewpointsHeadModel,
  getTeamSoloHeadModel,
  getTeamViewpointsHeadModel,
  getTeamEffectivenessIndividualHeadModel,
} = useDiamondHeadModel();

const remote = computed(
  () =>
    !["TeamEffectivenessAggregateDiamond", "TeamEffectivenessIndividualDiamond"].includes(
      props.kind,
    ),
);

const headModel = ref<HeadApiModel | null>(null);

const clickableHref = computed(() => {
  // eslint-disable-next-line no-script-url
  return props.clickable ? "javascript:void(0)" : "";
});

const altRatings = ref<Ratings>(null);

const leaderTypes = [
  "Professional",
  "Entrepreneurial",
  "Executional",
  "Practical",
  "Strategist",
  "Servant",
  "Transformational",
  "Charismatic",
  "Innovator",
];

function click(leaderType: string): void {
  if (props.clickable) {
    emit("leader-type-selection", leaderType);
  }
}

function css(leaderType: string): any[] {
  return [
    `btn-${leaderType.toLowerCase()} leaderType-${leaderType.toLowerCase()}`,
    {
      "team-diamond-head-shape": props.kind === "TeamEffectivenessAggregateDiamond",
    },
  ];
}

function style(leaderType: string): Record<string, any> {
  if (!(altRatings.value || props.ratings)) {
    return {
      display: "none",
    };
  }
  const axisScale = 18;
  const spotScale = 0.3;
  const positions = {
    Innovator: { x: 0, y: -2 },
    Entrepreneurial: { x: -1, y: -1 },
    Executional: { x: -2, y: -0 },
    Practical: { x: -1, y: 1 },
    Strategist: { x: 0, y: 2 },
    Servant: { x: 1, y: 1 },
    Transformational: { x: 2, y: 0 },
    Charismatic: { x: 1, y: -1 },
    Professional: { x: 0, y: 0 },
  };
  const x = positions[leaderType].x * axisScale;
  const y = positions[leaderType].y * axisScale;
  const sizePercent = (altRatings.value || props.ratings!).fits[leaderType] * spotScale;
  return {
    left: `${x - sizePercent * 0.5 + 50}%`,
    top: `${y - sizePercent * 0.5 + 50}%`,
    width: `${sizePercent}%`,
    height: `${sizePercent}%`,
    zIndex: 2,
    position: "absolute",
    opacity: 0.9,
  };
}

const doc = ref<CmsTeamEffectivenessReview | null>(null);

function tooltip(leaderType: string): string {
  if (!props.tooltips) {
    return ""; // Empty tooltips are not displayed at all;
  }
  const x: any = altRatings.value || props.ratings!;
  const y = x.ratings || x.meanRatings;
  const z = y[leaderType];
  const text = cms.asText(
    doc.value?.data.body.find((slice) => slice.primary.question_id === leaderType)?.primary
      .question_title,
  );
  return `${text}: ${z.toFixed(1)}`;
}

function getDotsByDescendingSize(dots: HTMLElement[]): HTMLElement[] {
  return dots.slice().sort((a, b) => {
    if (!a.style.width) {
      return -1;
    }
    if (!a.style.width) {
      return 1;
    }
    if (a.style.width < b.style.width) {
      return 1;
    }
    if (a.style.width > b.style.width) {
      return -1;
    }
    return 0;
  });
}

function runEntranceAnimation(): void {
  const dots: HTMLElement[] = getDotsByDescendingSize(
    Array.prototype.slice.call(diamondButtonRef.value?.getElementsByClassName("diamondButton")),
  );

  const timeline = new TimelineMax();
  for (let i = 0; i < dots.length; i++) {
    timeline.from(dots[i], 0.3, { height: 0, width: 0, delay: i * 0.2, ease: Back.easeOut }, 0);
  }
}

function emitDataLoaded(hasData: boolean): void {
  setTimeout(() => {
    emit("onDataLoaded", hasData);
    if (props.animateEntrance) {
      runEntranceAnimation();
    }
  }, 0);
}

function updateHtml(newKind: string): void {
  if (remote.value) {
    let modelPromise: Promise<HeadApiModel | null>;
    switch (props.kind) {
      case "solo":
        modelPromise = getSoloHeadModel();
        break;
      case "viewpoints":
        modelPromise = getViewpointsHeadModel();
        break;
      case "teamsolo":
        modelPromise = getTeamSoloHeadModel(props.headId || undefined);
        break;
      case "teamviewpoints":
        modelPromise = getTeamViewpointsHeadModel(props.headId || undefined);
        break;
      default:
        throw new Error(`Unexpected remote head kind: ${props.kind}`);
    }
    modelPromise.then((model) => {
      if (props.kind === newKind) {
        if (model) {
          model.clickable = props.clickable;
          model.showTooltips = props.tooltips;
          model.animateEntrance = props.animateEntrance;
        }
        headModel.value = model;
        emitDataLoaded(!!model);
      }
    });
  } else if (props.kind === "TeamEffectivenessIndividualDiamond") {
    // Nothing to do - ratings are passed in as a prop
  } else if (props.kind === "TeamEffectivenessAggregateDiamond") {
    getTeamEffectivenessReport(props.headId || undefined).then((data) => {
      altRatings.value = data.aggregateRatings;
      const hasData = data.questionnaires && data.questionnaires.length > 0;
      emitDataLoaded(hasData);
    });
  }
  if (
    props.kind === "TeamEffectivenessAggregateDiamond" ||
    props.kind === "TeamEffectivenessIndividualDiamond"
  ) {
    cms
      .getByUid("cms-team-effectiveness-review", cmsContentMap.teamEffectiveness.questionnaireUid)
      .then((d) => {
        doc.value = d;
      });
  }
}

watch(() => props.kind, updateHtml, { immediate: true });


return (_ctx: any,_cache: any) => {
  return (headModel.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "diamondButtonRef",
        ref: diamondButtonRef
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(HeadFromModel)
        ])
      ], 512))
    : (remote.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "diamond-head-wrapper",
          innerHTML: html,
          ref_key: "diamondButtonRef",
          ref: diamondButtonRef
        }, null, 512))
      : (_openBlock(), _createElementBlock("div", {
          key: 2,
          ref_key: "diamondButtonRef",
          ref: diamondButtonRef
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["diamond-head", {
        'diamond-head-solo': _ctx.kind === 'solo' || _ctx.kind === 'teamsolo',
        'diamond-head-team-effectiveness':
          _ctx.kind === 'TeamEffectivenessAggregateDiamond' ||
          _ctx.kind === 'TeamEffectivenessIndividualDiamond',
        'no-click-diamond-head': !_ctx.clickable,
      }])
          }, [
            _hoisted_2,
            _createElementVNode("div", _hoisted_3, [
              _hoisted_4,
              _hoisted_5,
              (altRatings.value || _ctx.ratings)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(leaderTypes, (leaderType, index) => {
                      return _createVNode(_unref(Tooltip), {
                        tag: "div",
                        key: index,
                        text: tooltip(leaderType)
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("a", {
                            style: _normalizeStyle(style(leaderType)),
                            class: _normalizeClass(["diamondButton leaderTypeTabButton diamondButton-circular", css(leaderType)]),
                            onClick: ($event: any) => (click(leaderType)),
                            href: clickableHref.value,
                            "aria-label": `Leader type: ${leaderType}`
                          }, _hoisted_9, 14, _hoisted_7)
                        ]),
                        _: 2
                      }, 1032, ["text"])
                    }), 64))
                  ]))
                : _createCommentVNode("", true)
            ])
          ], 2)
        ], 512))
}
}

})